import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, Car } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import { getContent, getContentList, saveContent, type ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0)
const numVal = (s: string) => (Number.isFinite(Number(s)) ? Number(s) : 0)

/* ── format <-> storage helpers (shared with the duration/transport editors) ── */
const isoToDate = (v: unknown): string => {
  const m = String(v ?? '').match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}
const dateToIso = (d: string): string => (d ? `${d}T00:00:00Z` : '')
const hhmm = (v: unknown): string => {
  const m = String(v ?? '').match(/(\d{2}:\d{2})/)
  return m ? m[1] : ''
}
function htmlToText(html: unknown): string {
  return String(html ?? '')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+/g, ' ')
    .trim()
}
function textToHtml(text: string): string {
  const t = text.replace(/\r\n/g, '\n').trim()
  if (!t) return ''
  return t
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<p>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
    .join('')
}

const WEEKDAYS: [string, string][] = [
  ['1', 'Lun'],
  ['2', 'Mar'],
  ['3', 'Mer'],
  ['4', 'Gio'],
  ['5', 'Ven'],
  ['6', 'Sab'],
  ['7', 'Dom'],
]
const LANGS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
] as const
type Lang = (typeof LANGS)[number]['code']

type Trans = { label: string; ctaLabel: string; description: string; confirmTitle: string; confirmText: string }
type Validity = { fromDay: string; toDay: string; fromTime: string; toTime: string; weekDays: string[] }
type Form = {
  name: string
  type: string
  enabled: boolean
  validity: Validity
  tr: Record<Lang, Trans>
}
type Loaded = Record<Lang, ContentEntity>
type CashRow = { id: string; name: string }
type TabKey = 'general' | 'validity' | 'cash' | 'texts'

function transOf(e: ContentEntity): Trans {
  const el = e.elements as El
  return {
    label: String(el.label ?? ''),
    ctaLabel: String(el.ctaLabel ?? ''),
    description: htmlToText(el.description),
    confirmTitle: String(el.confirmPageTitle ?? ''),
    confirmText: htmlToText(el.confirmPageText),
  }
}
function seedForm(loaded: Loaded): Form {
  const el = loaded.it.elements as El
  const v = (el.validity ?? {}) as El
  return {
    name: String(el.name ?? ''),
    type: String(el.type ?? '').toUpperCase(),
    enabled: isYes(el.enabled),
    validity: {
      fromDay: isoToDate(v.fromDay),
      toDay: isoToDate(v.toDay),
      fromTime: hhmm(v.fromTime),
      toTime: hhmm(v.toTime),
      weekDays: String(v.weekDays ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    },
    tr: { it: transOf(loaded.it), en: transOf(loaded.en), de: transOf(loaded.de) },
  }
}
// daily cash cap to show for a transport — the TRUE stored value (9999 = illimitato)
function cashSeedOf(el: El): string {
  return String(num(el.maxOnsitePaymentAvailability))
}

/* ── field atoms ── */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-faint">{hint}</span>}
    </label>
  )
}
const inputCls =
  'h-9 w-full rounded-md border border-line bg-card px-3 text-[13px] text-coal outline-none focus:border-brand-600 disabled:opacity-50'
const areaCls =
  'min-h-[64px] w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-coal outline-none focus:border-brand-600 disabled:opacity-50'

export function PaymentModeEditDrawer({ paymentModeId, onClose }: { paymentModeId: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<TabKey>('general')
  const [lang, setLang] = useState<Lang>('it')
  const [form, setForm] = useState<Form | null>(null)
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [cash, setCash] = useState<Record<string, string>>({})
  const [cashSeed, setCashSeed] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['paymentMode', paymentModeId, 'i18n'],
    queryFn: async (): Promise<Loaded> => {
      const [it, en, de] = await Promise.all([
        getContent('paymentMode', paymentModeId!, 'it'),
        getContent('paymentMode', paymentModeId!, 'en'),
        getContent('paymentMode', paymentModeId!, 'de'),
      ])
      return { it, en, de }
    },
    enabled: Boolean(paymentModeId),
  })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (detail.data) {
      setLoaded(detail.data)
      setForm(seedForm(detail.data))
      setTab('general')
      setLang('it')
      setError(null)
    }
  }, [detail.data])

  // seed the per-vehicle cash caps from the live transports whenever the drawer (re)opens
  useEffect(() => {
    if (!transports.data) return
    const seed: Record<string, string> = {}
    for (const t of transports.data.data) seed[t.id] = cashSeedOf(t.elements as El)
    setCash(seed)
    setCashSeed(seed)
  }, [transports.data, paymentModeId])

  const cashRows = useMemo<CashRow[]>(
    () =>
      (transports.data?.data ?? [])
        .map((t) => ({ id: String(t.id), name: String((t.elements as El).name ?? (t.elements as El).label ?? `Veicolo ${t.id}`) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [transports.data],
  )

  if (!paymentModeId) return null
  const isOnsite = form?.type === 'ONSITE'
  const tabList: { key: TabKey; label: string }[] = [
    { key: 'general', label: 'Generale' },
    { key: 'validity', label: 'Disponibilità' },
    ...(isOnsite ? [{ key: 'cash' as TabKey, label: 'Posti in cassa' }] : []),
    { key: 'texts', label: 'Testi' },
  ]
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchV = (p: Partial<Validity>) => setForm((f) => (f ? { ...f, validity: { ...f.validity, ...p } } : f))
  const patchTr = (l: Lang, p: Partial<Trans>) =>
    setForm((f) => (f ? { ...f, tr: { ...f.tr, [l]: { ...f.tr[l], ...p } } } : f))
  const toggleWeekday = (d: string) =>
    setForm((f) => {
      if (!f) return f
      const has = f.validity.weekDays.includes(d)
      const weekDays = has ? f.validity.weekDays.filter((x) => x !== d) : [...f.validity.weekDays, d].sort()
      return { ...f, validity: { ...f.validity, weekDays } }
    })

  function applyShared(e: El) {
    e.name = form!.name.trim()
    e.type = form!.type // preserved verbatim — ONLINE/ONSITE is not user-editable
    e.enabled = form!.enabled
    e.validity = {
      fromDay: dateToIso(form!.validity.fromDay),
      toDay: dateToIso(form!.validity.toDay),
      fromTime: form!.validity.fromTime || '',
      toTime: form!.validity.toTime || '',
      weekDays: form!.validity.weekDays.join(','),
    }
  }

  async function save() {
    if (!form || !loaded) return
    setError(null)
    setSaving(true)
    try {
      // 1) the payment mode itself (round-trip per language)
      for (const { code } of LANGS) {
        const base = loaded[code]
        const next: ContentEntity = { ...base, elements: { ...(base.elements as El) } }
        const e = next.elements as El
        applyShared(e)
        e.label = form.tr[code].label.trim() || form.name.trim()
        e.ctaLabel = form.tr[code].ctaLabel.trim()
        e.description = textToHtml(form.tr[code].description)
        e.confirmPageTitle = form.tr[code].confirmTitle.trim()
        e.confirmPageText = textToHtml(form.tr[code].confirmText)
        await saveContent('paymentMode', next, code)
      }

      // 2) per-vehicle cash caps — only the rows the user actually changed (ONSITE only)
      let touchedTransport = false
      if (isOnsite) {
        for (const r of cashRows) {
          const next = (cash[r.id] ?? '').trim()
          if (next === '' || next === cashSeed[r.id]) continue
          const val = numVal(next)
          if (val < 0) continue
          const full = await getContent('transport', r.id)
          const t: ContentEntity = { ...full, elements: { ...(full.elements as El), maxOnsitePaymentAvailability: val } }
          await saveContent('transport', t)
          touchedTransport = true
        }
      }

      await qc.invalidateQueries({ queryKey: ['content', 'paymentMode'] })
      await qc.invalidateQueries({ queryKey: ['paymentMode', paymentModeId] })
      if (touchedTransport) {
        await qc.invalidateQueries({ queryKey: ['content', 'transport'] })
        await qc.invalidateQueries({ queryKey: ['opt', 'transport'] })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer
      open={Boolean(paymentModeId)}
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">Modifica metodo di pagamento</span>}
      subtitle={form ? `${form.name || '—'} · ${form.type || '—'}` : 'Caricamento…'}
      footer={
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Salva scrive sul gestionale live.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50">
              Annulla
            </button>
            <button onClick={save} disabled={saving || !form} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
            </button>
          </div>
        </div>
      }
    >
      {detail.isLoading || !form ? (
        <div className="flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : detail.isError ? (
        <div className="py-20 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il metodo di pagamento.
        </div>
      ) : (
        <>
          <div className="mb-5 flex gap-1 border-b border-line">
            {tabList.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors',
                  tab === key ? 'border-brand-600 text-coal' : 'border-transparent text-muted hover:text-coal',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── GENERALE ── */}
          {tab === 'general' && (
            <div className="space-y-4">
              <Field label="Nome" hint="Identificativo interno (non tradotto)">
                <input className={inputCls} value={form.name} disabled={saving} onChange={(e) => patch({ name: e.target.value })} />
              </Field>
              <Field label="Tipo" hint="Online (carta / PayPal) oppure in cassa — non modificabile">
                <span className="inline-flex items-center rounded-md bg-paper px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wide text-muted ring-1 ring-line">
                  {form.type || '—'}
                </span>
              </Field>
              <label className="flex items-start gap-3 rounded-lg border border-line bg-card px-3.5 py-3">
                <input type="checkbox" checked={form.enabled} disabled={saving} onChange={(e) => patch({ enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand-600" />
                <span>
                  <span className="block text-[13px] font-semibold text-coal">Attivo</span>
                  <span className="block text-[12px] text-muted">
                    {isOnsite
                      ? 'Se disattivo, “Paga in cassa” non compare nell’app né sul sito.'
                      : 'Se disattivo, questo metodo non compare in fase di pagamento.'}
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* ── DISPONIBILITÀ (validity) ── */}
          {tab === 'validity' && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-muted">
                Finestra in cui questo metodo è proposto al cliente. Lascia tutto vuoto per “sempre disponibile”.
                {isOnsite && ' Il numero di posti pagabili in cassa si imposta nella scheda “Posti in cassa”.'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dal giorno">
                  <input type="date" className={inputCls} value={form.validity.fromDay} disabled={saving} onChange={(e) => patchV({ fromDay: e.target.value })} />
                </Field>
                <Field label="Al giorno">
                  <input type="date" className={inputCls} value={form.validity.toDay} disabled={saving} onChange={(e) => patchV({ toDay: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dalle ore">
                  <input type="time" className={inputCls} value={form.validity.fromTime} disabled={saving} onChange={(e) => patchV({ fromTime: e.target.value })} />
                </Field>
                <Field label="Fino alle ore">
                  <input type="time" className={inputCls} value={form.validity.toTime} disabled={saving} onChange={(e) => patchV({ toTime: e.target.value })} />
                </Field>
              </div>
              <Field label="Giorni della settimana" hint="Nessuno selezionato = tutti i giorni">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map(([val, lbl]) => {
                    const on = form.validity.weekDays.includes(val)
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={saving}
                        onClick={() => toggleWeekday(val)}
                        className={cn('rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition-colors', on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-card text-muted hover:text-coal')}
                      >
                        {lbl}
                      </button>
                    )
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* ── POSTI IN CASSA (per-vehicle daily cap, ONSITE only) ── */}
          {tab === 'cash' && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-muted">
                Numero massimo di prenotazioni pagabili in cassa <strong>al giorno</strong>, per tipo di veicolo. È il
                limite predefinito; per un singolo giorno puoi sovrascriverlo nella pagina <strong>Disponibilità</strong>.
                Questi valori sono gli stessi della pagina “Tipi di veicolo”. <strong>9999 = illimitato</strong>.
              </p>
              {transports.isLoading ? (
                <div className="flex items-center justify-center py-10 text-muted">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : cashRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted">Nessun tipo di veicolo configurato.</p>
              ) : (
                cashRows.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 rounded-lg border border-line bg-card px-3.5 py-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-paper text-slate ring-1 ring-line">
                      <Car className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-[13px] font-semibold text-coal">{r.name}</span>
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      disabled={saving}
                      value={cash[r.id] ?? ''}
                      onChange={(e) => setCash((c) => ({ ...c, [r.id]: e.target.value }))}
                      className="h-9 w-24 rounded-md border border-line bg-paper px-2 text-right font-mono text-[13px] text-coal outline-none focus:border-brand-600 disabled:opacity-50"
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── TESTI (per lingua) ── */}
          {tab === 'texts' && (
            <div className="space-y-3">
              <div className="flex gap-1 border-b border-line">
                {LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    className={cn(
                      '-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                      lang === code ? 'border-brand-600 text-coal' : 'border-transparent text-muted hover:text-coal',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Field label="Label" hint="Nome mostrato al cliente (vuoto = usa il Nome)">
                <input className={inputCls} value={form.tr[lang].label} disabled={saving} onChange={(e) => patchTr(lang, { label: e.target.value })} />
              </Field>
              <Field label="CTA acquisto" hint="Testo del pulsante (es. “Paga in cassa”)">
                <input className={inputCls} value={form.tr[lang].ctaLabel} disabled={saving} onChange={(e) => patchTr(lang, { ctaLabel: e.target.value })} />
              </Field>
              <Field label="Descrizione" hint="Testo semplice — una riga per paragrafo">
                <textarea className={areaCls} value={form.tr[lang].description} disabled={saving} onChange={(e) => patchTr(lang, { description: e.target.value })} />
              </Field>
              <Field label="Titolo pagina di conferma">
                <input className={inputCls} value={form.tr[lang].confirmTitle} disabled={saving} onChange={(e) => patchTr(lang, { confirmTitle: e.target.value })} />
              </Field>
              <Field label="Testo pagina di conferma" hint="Mostrato dopo la prenotazione">
                <textarea className={areaCls} value={form.tr[lang].confirmText} disabled={saving} onChange={(e) => patchTr(lang, { confirmText: e.target.value })} />
              </Field>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}
